// File: /opt/dev_environment/assets/workspace/app-init/src/services/authService.js

/**
 * Authentication Service
 * Manages JWT tokens (access + refresh) in localStorage.
 * Provides login, register, logout, token refresh, and user profile operations.
 */

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const BASE_URL = `${API_BASE}/api/v1/auth`;

const TOKEN_KEY = 'vokabelkasten_auth';

// ============================================
// TOKEN MANAGEMENT
// ============================================

class AuthService {
  /**
   * Store tokens after login or refresh.
   */
  static storeTokens(tokens) {
    const tokenData = {
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      expiresAt: Date.now() + (tokens.expires_in * 1000),
    };
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenData));
  }

  /**
   * Get the current access token, or null if expired.
   */
  static getAccessToken() {
    const data = this._getTokenData();
    if (!data) return null;

    // Check if token is expired (with 30 second buffer)
    if (Date.now() >= data.expiresAt - 30000) {
      return null; // Token expired, need refresh
    }

    return data.accessToken;
  }

  /**
   * Get the refresh token.
   */
  static getRefreshToken() {
    const data = this._getTokenData();
    return data?.refreshToken || null;
  }

  /**
   * Check if user has any valid authentication (access or refresh token).
   */
  static isAuthenticated() {
    return this.getAccessToken() !== null || this.getRefreshToken() !== null;
  }

  /**
   * Clear all tokens (logout).
   */
  static clearTokens() {
    localStorage.removeItem(TOKEN_KEY);
  }

  /**
   * Internal: parse stored token data.
   */
  static _getTokenData() {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) return null;

    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  // ============================================
  // AUTH API CALLS
  // ============================================

  /**
   * Register a new user.
   */
  static async register({ email, password, full_name }) {
    const response = await fetch(`${BASE_URL}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        full_name: full_name || undefined,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new AuthError(data.detail || 'Registrierung fehlgeschlagen', response.status);
    }

    return data;
  }

  /**
   * Login and store tokens.
   */
  static async login({ email, password }) {
    const response = await fetch(`${BASE_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new AuthError(data.detail || 'Anmeldung fehlgeschlagen', response.status);
    }

    this.storeTokens(data);
    return data;
  }

  /**
   * Refresh the access token using the refresh token.
   */
  static async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      throw new AuthError('Kein Refresh-Token verfügbar', 401);
    }

    const response = await fetch(`${BASE_URL}/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      this.clearTokens();
      throw new AuthError('Token-Aktualisierung fehlgeschlagen', response.status);
    }

    const tokens = await response.json();
    this.storeTokens(tokens);
    return tokens.access_token;
  }

  /**
   * Logout: revoke tokens on server and clear local storage.
   */
  static async logout() {
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getAccessToken();

    try {
      if (refreshToken && accessToken) {
        await fetch(`${BASE_URL}/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      }
    } catch {
      // Ignore errors during logout — we clear tokens regardless
    } finally {
      this.clearTokens();
    }
  }

  /**
   * Request a password reset email.
   */
  static async requestPasswordReset(email) {
    const response = await fetch(`${BASE_URL}/password-reset/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new AuthError('Anfrage fehlgeschlagen', response.status);
    }

    return response.json();
  }

  /**
   * Confirm a password reset with token and new password.
   */
  static async confirmPasswordReset(token, newPassword) {
    const response = await fetch(`${BASE_URL}/password-reset/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new AuthError(data.detail || 'Zurücksetzen fehlgeschlagen', response.status);
    }

    return data;
  }

  /**
   * Change password (requires authentication).
   */
  static async changePassword(currentPassword, newPassword) {
    const accessToken = await this._getValidAccessToken();

    const response = await fetch(`${BASE_URL}/password/change`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new AuthError(data.detail || 'Passwortänderung fehlgeschlagen', response.status);
    }

    return data;
  }

  /**
   * Verify email with token.
   */
  static async verifyEmail(token) {
    const response = await fetch(`${BASE_URL}/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new AuthError(data.detail || 'Verifizierung fehlgeschlagen', response.status);
    }

    return data;
  }

  /**
   * Resend verification email.
   */
  static async resendVerificationEmail(email) {
    const response = await fetch(`${BASE_URL}/verify-email/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new AuthError(data.detail || 'Erneutes Senden fehlgeschlagen', response.status);
    }

    return data;
  }

  /**
   * Get current user profile.
   */
  static async getProfile() {
    const accessToken = await this._getValidAccessToken();

    const response = await fetch(`${BASE_URL}/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new AuthError(data.detail || 'Profil konnte nicht geladen werden', response.status);
    }

    return data;
  }

  /**
   * Update user profile.
   */
  static async updateProfile(updates) {
    const accessToken = await this._getValidAccessToken();

    const response = await fetch(`${BASE_URL}/me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(updates),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new AuthError(data.detail || 'Profil konnte nicht aktualisiert werden', response.status);
    }

    return data;
  }

  /**
   * Deactivate account.
   */
  static async deactivateAccount() {
    const accessToken = await this._getValidAccessToken();

    const response = await fetch(`${BASE_URL}/me`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new AuthError(data.detail || 'Konto konnte nicht deaktiviert werden', response.status);
    }

    this.clearTokens();
  }

  /**
   * Internal: get a valid access token, refreshing if necessary.
   */
  static async _getValidAccessToken() {
    let accessToken = this.getAccessToken();

    if (!accessToken) {
      // Try to refresh
      accessToken = await this.refreshAccessToken();
    }

    return accessToken;
  }
}

/**
 * Custom error class for auth errors.
 */
class AuthError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
    this.name = 'AuthError';
  }
}

export { AuthService, AuthError };
export default AuthService;